import React from "react";

import { addEmployeesURL, coverageOptionsURL, companyInformationURL, documentsUploadPageURL, paymentURL, applicationReadyURL } from "EmployerApplication/urls";

import { BodySmall, BodyMedium } from "Cobalt/BodyText";
import Button from "Common/Button";
import { Col, Row } from "Common/Grid";
import { Surface, SurfaceHeader, SurfaceSection } from "Theme/Surface";

const QUESTIONS = {
  WHY_INFORMATION_NEEDED: {
    question: "Why is this information needed?",
    answer:
      "The insurance company uses these reports to confirm the validity of your business, and to ensure all of the employees who are enrolling actually work at your company.",
  },
  CANT_FIND_DOCUMENT: {
    question: "What if I can't find these?",
    answer:
      "Your accountant or payroll provider should have these documents, but if you aren't sure or can't find them- use the \"What if I don't have this\" link above.",
  },
  DOCUMENT_USAGE: {
    question: "How are these documents used?",
    answer:
      "These documents are used only to confirm your company's small group insurance eligibility at this initial enrollment. They aren't retained for any future use.",
  },
  ID_CARD: {
    question: "When will I have my ID card?",
    answer: "It takes 7-14 days for the application to be approved. After that- electronic ID cards will be available within 1-3 days.",
  },
  ANYTHING_ELSE: {
    question: "Do I need to do anything else?",
    answer:
      "No- our team will review your application and  reach out to you if there's anything missing, so just keep an eye on your email for requests from SimplyInsured.",
  },
  APPROVED: {
    question: "Will I definitely be approved?",
    answer:
      "Our team will do everything we can to get your coverage approved. Some very small businesses don't qualify for group insurance- if that's the case our team will be in touch with you within 1-2 business days.",
  },
  BANK_INFO_REQUIREMENT: {
    question: "Why do you need my bank information?",
    answer:
      "You need to pay the first month's premium bill up front in order to secure health insurance; payment will only be debited if your insurance is approved.",
  },
  PAYMENT_OPTIONS: {
    question: "Can I pay by paper check or a different method?",
    answer: "You must pay the first month via ACH by entering your bank information here, but if you want, you can pay future months via check.",
  },
  EMPLOYEE_PAYMENT: {
    question: "How do employees pay their share of the cost?",
    answer:
      "You pay the full premium, and then your employees reimburse you for their share via payroll deductions (you reduce their pay by the monthly amount).",
  },
  NO_EMAIL_RECIEVED: {
    question: "What if my employees didn't get the email?",
    answer:
      'Check their email addresses above, and then click "Send" email reminder. If the address is correct, you can have your employees check their spam folders or search for SimplyInsured in their inbox.',
  },
  APPLICATION_TIME: {
    question: "How long does the application take?",
    answer: "Each employee application takes about 2-5 minutes, depending on the number of family members the employee has.",
  },
  EMPLOYEES_FINISH: {
    question: "Must all employees finish before I can enroll?",
    answer: "Yes- the insurance company will require that all full-time employees are accounted for with an application or a waiver.",
  },
  DECLINE_COVERAGE: {
    question: "What if some of my employees don't want coverage?",
    answer: "If they're full-time, you should still add them here. Don't worry- they'll be able to decline coverage via a short waiver.",
  },
  NO_EMAIL_ACCOUNT: {
    question: "What about employees without email or internet access?",
    answer: "If your employees don't have access to a computer, you can fill the application out with them or for them, from your account.",
  },
  PART_TIME_EMPLOYEES: {
    question: "Do I need to add my part-time employees and contractors?",
    answer: "No- you don't have to offer them coverage, but if you do want to offer coverage to part time employees or contractors, you should add them here.",
  },
  NEXT_STEPS: {
    question: "What happens next?",
    answer:
      "After you finish the company application, your employees will fill out individual applications, you'll provide some basic business documentation, and provide payment information for the first month's payment.",
  },
  LATER_CHANGES: {
    question: "Can I make changes later?",
    answer:
      "If you want to make changes to these settings later, our team can help, but if you're unsure of your plan choice, consider revisiting your quote to finalize your decision.",
  },
  PROCESS_TIME: {
    question: "How long does the process take?",
    answer:
      "You can finish the application in about 10 minutes, and then it will take roughly 7-14 days to receive formal approval from the insurance carrier.",
  },
  WHO_PAYS: {
    question: "Who pays the bill?",
    answer:
      'Your company will pay the monthly bill, and then the employees\' share will be held out of their paycheck via payroll deduction. We will help you set those up. To see the cost split, click "See Cost Breakdown" on the plan.',
  },
  EMPLOYEE_PROCESS: {
    question: "How will my employees enroll?",
    answer:
      "Once you fill out the company application, your employees will receive a link in their email to sign up and we'll walk them through the process. It only takes 5 min per employee. If they don't have email, you can do it for them.",
  },
  EMPLOYEE_ENROLLMENT: {
    question: "Do employees have to enroll?",
    answer:
      "No. However, carriers have different rules about how many full-time employees must enroll or already have insurance coverage. In the unlikely event you run into participation issues, our team will help navigate these requirements to get approval.",
  },
};

const FactsAndQuestionsCard = () => {
  const questions = () => {
    if (window.location.pathname.indexOf(coverageOptionsURL) !== -1) {
      return [QUESTIONS.WHO_PAYS, QUESTIONS.EMPLOYEE_PROCESS, QUESTIONS.EMPLOYEE_ENROLLMENT];
    }
    if (window.location.pathname.indexOf(companyInformationURL) !== -1) {
      return [QUESTIONS.NEXT_STEPS, QUESTIONS.LATER_CHANGES, QUESTIONS.PROCESS_TIME];
    }
    if (window.location.pathname.indexOf(addEmployeesURL) !== -1) {
      return [QUESTIONS.DECLINE_COVERAGE, QUESTIONS.NO_EMAIL_ACCOUNT, QUESTIONS.PART_TIME_EMPLOYEES];
    }
    if (window.location.pathname.indexOf(documentsUploadPageURL) !== -1) {
      return [QUESTIONS.WHY_INFORMATION_NEEDED, QUESTIONS.CANT_FIND_DOCUMENT, QUESTIONS.DOCUMENT_USAGE];
    }
    if (window.location.pathname.indexOf(paymentURL) !== -1) {
      return [QUESTIONS.BANK_INFO_REQUIREMENT, QUESTIONS.PAYMENT_OPTIONS, QUESTIONS.EMPLOYEE_PAYMENT];
    }
    if (window.location.pathname.indexOf(applicationReadyURL) !== -1) {
      return [QUESTIONS.ID_CARD, QUESTIONS.ANYTHING_ELSE, QUESTIONS.APPROVED];
    }
  };

  if (questions()) {
    const numOfQuestions = questions().length;
    return (
      <Surface mt={6}>
        <SurfaceHeader>
          Questions{" "}
          <Button color="link" href="https://support.simplyinsured.com/" target="_blank" px={1}>
            (More)
          </Button>
        </SurfaceHeader>
        <SurfaceSection>
          <Row>
            {questions().map((question, i) => (
              <Col md={4} xs={12} key={i} mb={numOfQuestions - 1 === i ? 0 : 4}>
                <BodyMedium weight="bold" mb={3}>
                  {question.question}
                </BodyMedium>
                <BodyMedium>{question.answer}</BodyMedium>
              </Col>
            ))}
          </Row>
        </SurfaceSection>
      </Surface>
    );
  }

  return null;
};

export default FactsAndQuestionsCard;
